/**
 * <product-form> — the buy box's bundle selector and add-to-cart.
 *
 * Two jobs:
 *
 *  1. Bundle selection. Each tier is a radio whose value is the quantity to add. Picking
 *     one writes that quantity into the form's hidden `quantity` field and updates the
 *     price shown on the button, so the CTA always states what the click will cost.
 *
 *  2. Adding to the cart over AJAX. On submit it posts to /cart/add.js and then
 *     dispatches `cart:added`, which <cart-drawer> listens for to refresh and open
 *     itself. The page never navigates.
 *
 * Without JavaScript the form still works: it is a real <form> posting to /cart/add,
 * with a quantity field that defaults to the preselected tier.
 */

// Wrapped so nothing here reaches global scope: these are classic scripts, and a
// top-level name shared with another file makes the whole file throw before it can
// register its element. Also what makes a duplicate <script> tag for this file safe.
(() => {
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.quantityField = this.querySelector('[data-quantity-field]');
      this.addButton = this.querySelector('[data-add-button]');
      this.priceOutput = this.querySelector('[data-add-price]');
      this.radios = [...this.querySelectorAll('[data-bundle-selector] input[type="radio"]')];

      this.onSubmit = this.onSubmit.bind(this);
      this.form?.addEventListener('submit', this.onSubmit);

      this.radios.forEach((radio) => {
        radio.addEventListener('change', () => this.selectBundle(radio));
      });

      // Sync the hidden quantity with whichever tier Liquid pre-checked.
      const checked = this.radios.find((radio) => radio.checked);
      if (checked) this.selectBundle(checked);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
    }

    selectBundle(radio) {
      if (this.quantityField) this.quantityField.value = radio.value;
      if (this.priceOutput) this.priceOutput.textContent = radio.dataset.bundlePriceFormatted ?? '';
    }

    async onSubmit(event) {
      event.preventDefault();

      if (this.addButton?.disabled) return;

      this.setBusy(true);

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: new FormData(this.form),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description ?? `cart/add responded ${response.status}`);
        }

        document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true }));
      } catch (error) {
        console.error(error);
        // Fall back to a full page post so the customer is never stuck.
        this.form.submit();
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(isBusy) {
      if (!this.addButton) return;

      this.addButton.setAttribute('aria-busy', String(isBusy));
      this.addButton.classList.toggle('is-busy', isBusy);
    }
  }

  if (!customElements.get('product-form')) {
    customElements.define('product-form', ProductForm);
  }
})();
